import {
  Box,
  Divider,
  ListItemButton,
  Radio,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useMemo } from "react";
import { ProductCategories, ProductPrice } from "src/api/types";
import { useIsMobile } from "src/hooks/useIsMobile";
import { mapBreakdownToProduct } from "./utils/mapBreakdownToProduct";
import { useTranslation } from "react-i18next";
import { useProduct } from "src/context/ProductProvider";
import CustomChip from "src/shared/components/CustomChip";
interface ProductSelectionProps {
  item: ProductPrice;
  onChange?: (value: ProductPrice) => void;
  selectedItem: ProductPrice | undefined;
  page: string;
  prescribedPrice?: string;
  form?: string;
  count?: number;
}

const divisorByIntervalCount: Record<number, number> = {
  74: 3,
  30: 1,
  90: 3,
  150: 5,
  180: 6,
  84: 3,
  168: 6,
};

const ProductSelection = ({
  item,
  onChange,
  selectedItem,
  page,
  prescribedPrice,
  form,
  count = 0,
}: ProductSelectionProps) => {
  const isMobile = useIsMobile();
  const component = "productSelection";
  const { t } = useTranslation();
  const billingModel = useProduct((product) => product.billing_model);
  const subscriptionModel = useProduct(
    (product) => product.billing_model === "subscription",
  );

  const isSelected = useMemo(() => {
    return selectedItem?.name === item.name;
  }, [selectedItem, item]);

  const isQuarterly = useMemo(() => {
    return item.name.includes("Quarterly");
  }, [item]);

  const breakdown = useMemo(() => {
    const name = `${item.product.name} - ${item.interval_count}`;

    const productBreakdown = mapBreakdownToProduct[name];
    if (productBreakdown == null) return;

    return productBreakdown;
  }, [item]);

  const highlightedBreakdownIndex = 0;

  const divisor = useMemo(
    () => divisorByIntervalCount[item.interval_count],
    [item.interval_count],
  );

  const discountedPrice = useMemo(() => {
    const discount = item.coupon?.amount ?? 0;

    if (item.product.slug === "ed-ot") {
      const price = count ? item.amount * count : item.amount;

      const hasFraction = price % 1 !== 0;

      return price.toFixed(hasFraction ? 2 : 0);
    }

    if (item.product.slug === "mhl-ot" && form === "topical") {
      return item.amount - discount;
    }

    if (item.product.slug === "tr" && item.number_of_months == 3) {
      return item.first_month_price;
    }

    return divisor ? item.amount / divisor : item.amount - discount;
  }, [
    count,
    divisor,
    form,
    item.amount,
    item.coupon?.amount,
    item.first_month_price,
    item.number_of_months,
    item.product.slug,
  ]);

  const handleSelect = useCallback(() => {
    onChange?.(item);
  }, [item, onChange]);

  const displayMonthlyPrice = useMemo(
    () =>
      item.interval_count > 1 &&
      item.monthly_price != null &&
      item.monthly_price !== discountedPrice &&
      isSelected,
    [discountedPrice, isSelected, item.interval_count, item.monthly_price],
  );

  const header = useMemo(() => {
    if (
      item.product_category.name === ProductCategories.WeightLoss &&
      billingModel === "one_time"
    ) {
      return t(
        `${page}.${component}.${item.product_category.name.toLowerCase()}.${
          item.number_of_months
        }month.header`,
        item.number_of_months === 1 ? "4 weeks" : "3 months",
      );
    }

    return t(
      `${page}.${component}.${billingModel}.${item.product_category.name.toLowerCase()}.${item.name.toLowerCase()}.header`,
    );
  }, [
    billingModel,
    item.name,
    item.number_of_months,
    item.product_category.name,
    page,
    t,
  ]);

  const showChip = useMemo(() => {
    if (item.product.slug === "bc") {
      return item.number_of_months === 6;
    }

    if (
      ["fhl-ot-dropper"].includes(`${item.product.slug}-${form}`) ||
      item.product.slug === "ed-ot"
    ) {
      return item.number_of_months === 5;
    }

    return isQuarterly;
  }, [form, isQuarterly, item.number_of_months, item.product.slug]);

  const showDisclaimer = useMemo(() => {
    if (["mhl-ot", "fhl-ot", "ed-ot", "iwl-ot"].includes(item.product.slug)) {
      return true;
    }

    return subscriptionModel;
  }, [item.product.slug, subscriptionModel]);

  const disclaimer = useMemo(() => {
    if (["ed-ot", "iwl-ot"].includes(item.product.slug)) {
      return t(`${page}.${component}.${item.product.slug}.disclaimer`, {
        totalPills: count * item.number_of_months,
      });
    }

    return t(`${page}.${component}.${billingModel}.disclaimer`);
  }, [billingModel, count, item.number_of_months, item.product.slug, page, t]);

  const competitorPrice = useMemo(() => {
    if (item.product.slug === "ed-ot") {
      return (item.competitor_price || 0) * count;
    }

    return item.competitor_price;
  }, [count, item.competitor_price, item.product.slug]);

  const monthlyPrice = useMemo(() => {
    if (item.product.slug === "tr" && item.number_of_months == 3) {
      return item.amount / divisor;
    }

    return item.monthly_price;
  }, [
    divisor,
    item.amount,
    item.monthly_price,
    item.number_of_months,
    item.product.slug,
  ]);

  const displayPerMonthText = useMemo(
    () => item.monthly_price != null,
    [item.monthly_price],
  );

  return (
    <Stack>
      {showChip ? (
        <CustomChip
          label={t(
            `${page}.${component}.${item.product_category.name.toLowerCase()}.chip`,
          )}
        />
      ) : null}
      <ListItemButton
        sx={{
          gap: "12px",
          boxShadow: 2,
          border: isSelected ? "solid 2px #00B6B9" : "solid 1px #D3D6D8",
          borderRadius: "12px",
          p: 2,
          pl: 0,
          pr: 2,
        }}
        onClick={handleSelect}
      >
        <Radio
          icon={<Box sx={{ display: "none" }} />}
          checkedIcon={<Box sx={{ display: "none" }} />}
          checked={isSelected}
        />

        <Stack sx={{ width: "100%" }} gap={1}>
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Typography fontWeight={600}>{header}</Typography>
            <Stack direction="row" gap={1}>
              {displayMonthlyPrice ? (
                <Typography
                  sx={{ textDecoration: "line-through" }}
                  color="#56575B"
                >
                  ${monthlyPrice}
                </Typography>
              ) : null}
              {item.product.slug === "ed-ot" ? (
                <Typography
                  sx={{ textDecoration: "line-through" }}
                  color="#56575B"
                >
                  ${competitorPrice}
                </Typography>
              ) : null}
              <Typography
                sx={{ color: isSelected ? "#00B6B9" : "#000000" }}
                fontWeight={600}
              >
                ${discountedPrice}
                {displayPerMonthText
                  ? t(`${page}.${component}.perMonth`)
                  : null}
              </Typography>
            </Stack>
          </Box>
          {isSelected && breakdown != null ? <Divider /> : null}
          <Stack>
            {isSelected ? (
              <>
                {breakdown != null ? (
                  <>
                    {" "}
                    <Typography>
                      {" "}
                      {t(
                        `${page}.${component}.${billingModel}.planInformationHeader`,
                      )}
                    </Typography>
                    <Stack component={"ul"} gap={1}>
                      {breakdown.map((b, idx) => (
                        <li
                          key={idx}
                          style={{
                            color:
                              idx === highlightedBreakdownIndex
                                ? "#00B6B9"
                                : "#000000",
                          }}
                        >
                          <Typography
                            variant={isMobile ? "body2" : "body1"}
                            color={
                              idx === highlightedBreakdownIndex
                                ? "#00B6B9"
                                : "#000000"
                            }
                            fontWeight={
                              idx === highlightedBreakdownIndex ? 700 : 400
                            }
                          >
                            {t(b, {
                              price: isQuarterly
                                ? prescribedPrice
                                : item.monthly_price,
                            })}
                          </Typography>
                        </li>
                      ))}
                    </Stack>
                  </>
                ) : null}

                {showDisclaimer ? (
                  <Typography
                    textAlign="center"
                    variant="subtitle2"
                    fontSize={isMobile ? "0.8rem!important" : ""}
                    sx={{
                      background: "#FBFBFB",
                      borderRadius: "4px",
                      padding: "8px 14px",
                    }}
                  >
                    {disclaimer}
                  </Typography>
                ) : null}
              </>
            ) : null}
          </Stack>
        </Stack>
      </ListItemButton>
    </Stack>
  );
};

export default ProductSelection;
